'use client';

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  Building2,
  SlidersHorizontal,
  X,
  LayoutGrid,
  Map,
  GitCompareArrows,
  MapPin,
  Locate,
  Search,
} from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { PageHeader } from '@/shared/layout';
import { Card, Modal, Select } from '@/shared/ui';
import type { Hotel, CompareHotel, FilterState } from '../types/hotels.types';
import {
  POPULAR_LOCATIONS,
  RADIUS_OPTIONS,
  DEFAULT_RADIUS_KM,
  haversineDistanceKm,
} from './hotels.data';
import { HotelMapView } from './HotelMapView';
import { HotelCompare } from './HotelCompare';
import { SearchFilterPanel } from './SearchFilterPanel';
import { HotelCard } from './HotelCard';
import { useWishlist } from '../hooks/useWishlist';
import { usePublicHotels } from '../hooks/useHotels';
import { SkeletonCard } from '@/shared/feedback';
import {
  createQueryString,
  pickSearchParams,
  PUBLIC_RESULTS_QUERY_KEYS,
  withQuery,
} from '../hotel-routing';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Distance-enriched hotel                                                    */
/* -------------------------------------------------------------------------- */

interface HotelWithDistance extends Hotel {
  distanceKm: number | null;
}

/* -------------------------------------------------------------------------- */
/*  HotelSearchResults                                                         */
/* -------------------------------------------------------------------------- */

export function HotelSearchResults() {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: allHotels, isLoading: isLoadingHotels } = usePublicHotels();
  const searchHotels: Hotel[] = useMemo(() => allHotels ?? [], [allHotels]);
  const activeSearchTerm = searchParams.get('q')?.trim() ?? '';
  const dateQuery = useMemo(
    () => pickSearchParams(searchParams, PUBLIC_RESULTS_QUERY_KEYS),
    [searchParams],
  );
  const [searchDraft, setSearchDraft] = useState(activeSearchTerm);
  const [sortBy, setSortBy] = useState(() => searchParams.get('sort') || 'recommended');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'map'>(() => (searchParams.get('view') === 'map' ? 'map' : 'grid'));
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [showCompare, setShowCompare] = useState(false);
  const { isSaved, toggleSave } = useWishlist();
  const [filters, setFilters] = useState<FilterState>(() => {
    const starsParam = searchParams.get('stars');
    const typesParam = searchParams.get('types');
    const amenitiesParam = searchParams.get('amenities');
    return {
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
      starRatings: starsParam ? starsParam.split(',').map(Number).filter(Boolean) : [],
      propertyTypes: typesParam ? typesParam.split(',').filter(Boolean) : [],
      searchLocationSlug: searchParams.get('location') || '',
      radiusKm: parseInt(searchParams.get('radius') || '0', 10),
      amenities: amenitiesParam ? amenitiesParam.split(',').filter(Boolean) : [],
      minRating: parseFloat(searchParams.get('rating') || '0'),
    };
  });
  const filterSyncRef = useRef(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync external data into local editable state
    setSearchDraft(activeSearchTerm);
  }, [activeSearchTerm]);

  /* ----- Sync filters → URL ----- */
  useEffect(() => {
    if (!filterSyncRef.current) {
      filterSyncRef.current = true;
      return;
    }
    const params = new URLSearchParams();
    const q = searchParams.get('q');
    const checkIn = searchParams.get('checkIn');
    const checkOut = searchParams.get('checkOut');
    const guests = searchParams.get('guests');
    if (q) params.set('q', q);
    if (checkIn) params.set('checkIn', checkIn);
    if (checkOut) params.set('checkOut', checkOut);
    if (guests) params.set('guests', guests);
    if (filters.minPrice) params.set('minPrice', filters.minPrice);
    if (filters.maxPrice) params.set('maxPrice', filters.maxPrice);
    if (filters.starRatings.length) params.set('stars', filters.starRatings.join(','));
    if (filters.propertyTypes.length) params.set('types', filters.propertyTypes.join(','));
    if (filters.searchLocationSlug) params.set('location', filters.searchLocationSlug);
    if (filters.radiusKm) params.set('radius', String(filters.radiusKm));
    if (filters.amenities.length) params.set('amenities', filters.amenities.join(','));
    if (filters.minRating > 0) params.set('rating', String(filters.minRating));
    if (sortBy && sortBy !== 'recommended') params.set('sort', sortBy);
    if (viewMode !== 'grid') params.set('view', viewMode);
    const qs = params.toString();
    router.replace(qs ? `/hotels?${qs}` : '/hotels', { scroll: false });
  }, [filters, sortBy, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ----- Resolve search location ----- */
  const searchLocation = useMemo(
    () => POPULAR_LOCATIONS.find((l) => l.slug === filters.searchLocationSlug) ?? null,
    [filters.searchLocationSlug]
  );

  /* ----- Compare toggle handler ----- */
  const handleToggleCompare = useCallback((hotelId: string) => {
    setCompareIds((prev) => {
      if (prev.includes(hotelId)) return prev.filter((id) => id !== hotelId);
      if (prev.length >= 3) return prev;
      return [...prev, hotelId];
    });
  }, []);

  const handleRemoveCompare = useCallback((hotelId: string) => {
    setCompareIds((prev) => prev.filter((id) => id !== hotelId));
  }, []);

  /* ----- Quick location select (toolbar) ----- */
  const handleToolbarLocationChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const slug = e.target.value;
    setFilters((prev) => ({
      ...prev,
      searchLocationSlug: slug,
      radiusKm: slug ? (prev.radiusKm || DEFAULT_RADIUS_KM) : 0,
    }));
  }, []);

  const handleToolbarRadiusChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setFilters((prev) => ({
      ...prev,
      radiusKm: parseInt(e.target.value, 10) || DEFAULT_RADIUS_KM,
    }));
  }, []);

  const handleSearchSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const nextQuery = createQueryString({
      q: searchDraft,
      checkIn: searchParams.get('checkIn'),
      checkOut: searchParams.get('checkOut'),
      guests: searchParams.get('guests'),
    });

    router.push(withQuery('/hotels', nextQuery));
  }, [router, searchDraft, searchParams]);

  const handleClearSearch = useCallback(() => {
    setSearchDraft('');

    const nextQuery = createQueryString({
      checkIn: searchParams.get('checkIn'),
      checkOut: searchParams.get('checkOut'),
      guests: searchParams.get('guests'),
    });

    router.push(withQuery('/hotels', nextQuery));
  }, [router, searchParams]);

  /* ----- Filtering + distance ----- */
  const filteredHotels: HotelWithDistance[] = useMemo(() => {
    let results: HotelWithDistance[] = searchHotels.map((h) => {
      const dist = searchLocation
        ? haversineDistanceKm(searchLocation.latitude, searchLocation.longitude, h.latitude, h.longitude)
        : null;
      return { ...h, distanceKm: dist };
    });

    if (activeSearchTerm) {
      const normalizedSearch = activeSearchTerm.toLowerCase();
      results = results.filter((hotel) =>
        [hotel.name, hotel.location, hotel.type]
          .some((value) => value.toLowerCase().includes(normalizedSearch)),
      );
    }

    /* Radius filter */
    if (searchLocation && filters.radiusKm > 0) {
      results = results.filter((h) => h.distanceKm !== null && h.distanceKm <= filters.radiusKm);
    }

    /* Price filter */
    const min = filters.minPrice ? parseInt(filters.minPrice, 10) : 0;
    const max = filters.maxPrice ? parseInt(filters.maxPrice, 10) : Infinity;
    if (min > 0 || max < Infinity) {
      // An unpriced hotel matches no price band.
      //
      // `null >= 0` is TRUE in JS, so unpriced properties leaked into every
      // filtered result — including a "under Rs 2000" search they cannot be
      // known to satisfy. Excluding them is the honest answer: the user asked
      // for a price range and we do not know this hotel's price.
      results = results.filter(
        (h) => typeof h.price === 'number' && h.price >= min && h.price <= max,
      );
    }

    /* Star filter */
    if (filters.starRatings.length > 0) {
      results = results.filter((h) => filters.starRatings.includes(h.stars));
    }

    /* Property type filter */
    if (filters.propertyTypes.length > 0) {
      results = results.filter((h) => filters.propertyTypes.includes(h.type));
    }

    /* Amenity filter */
    if (filters.amenities.length > 0) {
      results = results.filter((h) =>
        filters.amenities.every((amenity) => h.amenities.includes(amenity)),
      );
    }

    /* Rating filter */
    if (filters.minRating > 0) {
      results = results.filter((h) => h.rating >= filters.minRating);
    }

    /* Sort */
    switch (sortBy) {
      case 'price-low':
        // Unpriced hotels sort LAST, not as if they cost 0.
        //
        // `null - null` is 0 and `5000 - null` is 5000, so "price: low to
        // high" put every unpriced hotel at the very top as though it were the
        // cheapest.
        results.sort((a, b) => {
          if (typeof a.price !== 'number') return 1;
          if (typeof b.price !== 'number') return -1;
          return a.price - b.price;
        });
        break;
      case 'price-high':
        // Unpriced last in BOTH directions. Sorting by price cannot rank a
        // hotel whose price is unknown, so it goes to the end either way
        // rather than being treated as 0 (top of low-to-high, bottom of
        // high-to-low, both wrong).
        results.sort((a, b) => {
          if (typeof a.price !== 'number') return 1;
          if (typeof b.price !== 'number') return -1;
          return b.price - a.price;
        });
        break;
      case 'rating':
        results.sort((a, b) => b.rating - a.rating);
        break;
      case 'distance':
        results.sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
        break;
      default:
        break;
    }

    return results;
  }, [activeSearchTerm, filters, sortBy, searchLocation, searchHotels]);

  /* ----- Build compare hotel list ----- */
  const compareHotels: CompareHotel[] = compareIds
    .map((id) => searchHotels.find((h) => h.id === id))
    .filter((h): h is Hotel => h !== undefined)
    .map((h) => ({
      id: h.id,
      name: h.name,
      city: h.location.split(',')[0].trim(),
      stars: h.stars,
      price: h.price,
      rating: h.rating,
      reviews: h.reviews,
      amenities: h.amenities,
      roomTypes: h.roomTypes,
    }));

  /* ----- Description with radius info ----- */
  const description = useMemo(() => {
    const parts = [`${filteredHotels.length} hotel${filteredHotels.length !== 1 ? 's' : ''}`];

    if (activeSearchTerm) {
      parts.push(`matching "${activeSearchTerm}"`);
    } else {
      parts.push('found');
    }

    if (searchLocation) {
      parts.push(`within ${filters.radiusKm || DEFAULT_RADIUS_KM} km of ${searchLocation.name}`);
    }

    return parts.join(' ');
  }, [activeSearchTerm, filteredHotels.length, filters.radiusKm, searchLocation]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <PageHeader
        id="hotels-search-header"
        title={activeSearchTerm ? t('hotelSearch.searchResultsFor', { query: activeSearchTerm }) : t('hotelSearch.searchResults')}
        description={description}
      />

      <form
        id="hotels-search-form"
        onSubmit={handleSearchSubmit}
        className={cn(
          'mt-5 flex flex-col gap-3 rounded-2xl border border-hms-border bg-hms-card p-4',
          'shadow-lg shadow-black/10 sm:flex-row sm:items-center',
        )}
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-hms-textDim" />
          <input
            id="hotels-search-input"
            type="text"
            value={searchDraft}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchDraft(e.target.value)}
            placeholder={t('hotelSearch.searchPlaceholder')}
            className={cn(
              'h-11 w-full rounded-xl border border-hms-border bg-hms-surface pl-10 pr-3',
              'text-sm text-hms-text placeholder:text-hms-textDim',
              'outline-none transition-colors focus:border-hms-gold/50 focus:ring-1 focus:ring-hms-gold/30',
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          {(activeSearchTerm || searchDraft) && (
            <button
              id="hotels-search-clear"
              type="button"
              onClick={handleClearSearch}
              className="inline-flex h-11 items-center justify-center rounded-xl border border-hms-border px-4 text-xs font-semibold text-hms-textSub transition-colors hover:border-hms-borderLt hover:text-hms-text"
            >
              {t('hotelSearch.clear')}
            </button>
          )}
          <button
            id="hotels-search-submit"
            type="submit"
            className="inline-flex h-11 items-center justify-center rounded-xl bg-hms-gold px-5 text-sm font-semibold text-black transition-all hover:bg-hms-goldLt"
          >
            {t('hotelSearch.updateSearch')}
          </button>
        </div>
      </form>

      {/* ---- Location & Radius Bar (near search) ---- */}
      <div
        id="hotels-search-location-bar"
        className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-hms-border bg-hms-card px-4 py-3"
      >
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
          <Locate className="h-3.5 w-3.5 text-hms-teal" />
          {t('hotelSearch.nearbySearch')}
        </div>

        {/* Location select */}
        <Select
          id="hotels-search-toolbar-location"
          value={filters.searchLocationSlug}
          onChange={handleToolbarLocationChange}
          placeholder={t('hotelSearch.allLocations')}
          options={POPULAR_LOCATIONS.map((loc) => ({ label: loc.name, value: loc.slug }))}
        />

        {/* Radius select */}
        {filters.searchLocationSlug && (
          <>
            <span className="text-[10px] text-hms-textDim">{t('hotelSearch.within')}</span>
            <Select
              id="hotels-search-toolbar-radius"
              value={String(filters.radiusKm || DEFAULT_RADIUS_KM)}
              onChange={handleToolbarRadiusChange}
              options={RADIUS_OPTIONS.map((opt) => ({ label: opt.label, value: String(opt.value) }))}
            />

            {/* Active location pill */}
            <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-1.5 rounded-md bg-hms-teal/10 px-2.5 py-1 text-[10px] font-medium text-hms-teal">
              <MapPin className="h-3 w-3" />
              {searchLocation?.name} &middot; {filters.radiusKm || DEFAULT_RADIUS_KM} km
              <button
                id="hotels-search-toolbar-clear-location"
                type="button"
                onClick={() => setFilters((prev) => ({ ...prev, searchLocationSlug: '', radiusKm: 0 }))}
                className="ml-1 rounded p-0.5 text-hms-textDim hover:text-hms-text"
                aria-label={t('hotelSearch.clearLocation')}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Toolbar: view toggle + sort + mobile filter toggle */}
      <div className="mt-4 flex flex-wrap items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          {/* Mobile filter button */}
          <button
            id="hotels-search-mobile-filter-btn"
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg border border-hms-border px-3 py-2',
              'text-xs font-medium text-hms-textSub lg:hidden',
              'hover:border-hms-borderLt hover:text-hms-text',
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {t('hotelSearch.filters')}
          </button>

          {/* View toggle */}
          <div
            id="hotels-search-view-toggle"
            className="inline-flex rounded-lg border border-hms-border bg-hms-surface p-0.5"
          >
            <button
              id="hotels-search-view-grid"
              type="button"
              onClick={() => setViewMode('grid')}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-medium transition-all',
                viewMode === 'grid'
                  ? 'bg-hms-gold/15 text-hms-gold'
                  : 'text-hms-textSub hover:text-hms-text',
              )}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              {t('hotelSearch.grid')}
            </button>
            <button
              id="hotels-search-view-map"
              type="button"
              onClick={() => setViewMode('map')}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-medium transition-all',
                viewMode === 'map'
                  ? 'bg-hms-gold/15 text-hms-gold'
                  : 'text-hms-textSub hover:text-hms-text',
              )}
            >
              <Map className="h-3.5 w-3.5" />
              {t('hotelSearch.map')}
            </button>
          </div>
        </div>

        {/* Sort */}
        <div className="w-full sm:w-auto sm:ml-auto">
          <Select
            id="hotels-search-sort"
            value={sortBy}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setSortBy(e.target.value)}
            options={[
              { label: t('hotelSearch.sortByRecommended'), value: 'recommended' },
              { label: t('hotelSearch.priceLowToHigh'), value: 'price-low' },
              { label: t('hotelSearch.priceHighToLow'), value: 'price-high' },
              { label: t('hotelSearch.rating'), value: 'rating' },
              ...(searchLocation ? [{ label: t('hotelSearch.distanceNearest'), value: 'distance' }] : []),
            ]}
          />
        </div>
      </div>

      {/* Main layout */}
      <div className="mt-6 flex gap-6">
        {/* Desktop sidebar */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <SearchFilterPanel filters={filters} onChange={setFilters} />
        </aside>

        {/* Content area */}
        <section className="min-w-0 flex-1">
          {isLoadingHotels ? (
            <div id="hotels-search-skeleton" className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : viewMode === 'map' ? (
            <HotelMapView
              hotels={filteredHotels}
              searchLocation={searchLocation}
              radiusKm={filters.radiusKm || 0}
            />
          ) : filteredHotels.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filteredHotels.map((hotel) => (
                <HotelCard
                  key={hotel.id}
                  hotel={hotel}
                  dateQuery={dateQuery}
                  distanceKm={hotel.distanceKm}
                  isCompareSelected={compareIds.includes(hotel.id)}
                  onToggleCompare={handleToggleCompare}
                  compareDisabled={compareIds.length >= 3}
                  isSaved={isSaved(hotel.id)}
                  onToggleSave={toggleSave}
                />
              ))}
            </div>
          ) : (
            <Card padding="lg" className="flex flex-col items-center justify-center py-20 text-center">
              <Building2 className="mb-4 h-12 w-12 text-hms-textDim" />
              <p className="text-sm font-semibold text-hms-text">{t('hotelSearch.noHotelsFound')}</p>
              <p className="mt-1 text-xs text-hms-textSub">
                {searchLocation
                  ? t('hotelSearch.noHotelsWithinRadius', { n: String(filters.radiusKm || DEFAULT_RADIUS_KM), location: searchLocation.name })
                  : t('hotelSearch.tryAdjustingFilters')}
              </p>
            </Card>
          )}
        </section>
      </div>

      {/* ---- Floating compare bar ---- */}
      {compareIds.length > 0 && (
        <div
          id="hotels-search-compare-bar"
          className={cn(
            'fixed bottom-0 left-0 right-0 z-40',
            'border-t border-hms-border bg-hms-card/95 backdrop-blur-md',
            'px-4 py-3 sm:px-6',
          )}
        >
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3">
              <GitCompareArrows className="h-4 w-4 text-hms-gold" />
              <span className="text-xs text-hms-textSub">
                <span className="font-semibold text-hms-text">{compareIds.length}</span>
                {' '}{t('hotelSearch.of3HotelsSelected')}
              </span>
              {/* Selected hotel pills */}
              <div className="hidden items-center gap-2 sm:flex">
                {compareHotels.map((h) => (
                  <span
                    key={h.id}
                    className="inline-flex items-center gap-1.5 rounded-md bg-white/5 px-2 py-1 text-[10px] font-medium text-hms-textSub"
                  >
                    {h.name}
                    <button
                      id={`hotels-search-compare-bar-remove-${h.id}`}
                      type="button"
                      onClick={() => handleRemoveCompare(h.id)}
                      className="rounded-sm text-hms-textDim hover:text-hms-red"
                      aria-label={`Remove ${h.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                id="hotels-search-compare-clear"
                type="button"
                onClick={() => setCompareIds([])}
                className={cn(
                  'inline-flex h-8 items-center rounded-lg px-3',
                  'text-[11px] font-medium text-hms-textSub',
                  'transition-colors hover:bg-white/5 hover:text-hms-text',
                )}
              >
                {t('hotelSearch.clear')}
              </button>
              <button
                id="hotels-search-compare-btn"
                type="button"
                onClick={() => setShowCompare(true)}
                disabled={compareIds.length < 2}
                className={cn(
                  'inline-flex h-8 items-center gap-1.5 rounded-lg px-4',
                  'text-[11px] font-semibold',
                  'transition-colors',
                  compareIds.length >= 2
                    ? 'bg-hms-gold text-black hover:bg-hms-goldLt'
                    : 'bg-hms-gold/30 text-hms-textDim cursor-not-allowed',
                )}
              >
                <GitCompareArrows className="h-3.5 w-3.5" />
                {t('hotelSearch.compare')} ({compareIds.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Compare Modal ---- */}
      <Modal
        id="hotels-search-compare-modal"
        open={showCompare}
        onClose={() => setShowCompare(false)}
        title={t('hotelSearch.compareHotels')}
        description={t('hotelSearch.compareHotelsDesc')}
        size="xl"
        className="max-w-5xl"
      >
        <HotelCompare
          hotels={compareHotels}
          onRemove={handleRemoveCompare}
        />
      </Modal>

      {/* ---- Mobile filter drawer ---- */}
      {mobileFiltersOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileFiltersOpen(false)}
          />

          {/* Drawer */}
          <div
            className={cn(
              'fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] overflow-y-auto',
              'border-r border-hms-border bg-hms-sidebar p-5 lg:hidden',
              'animate-in slide-in-from-left duration-300',
            )}
          >
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-hms-text">{t('hotelSearch.filters')}</h2>
              <button
                id="hotels-search-mobile-filter-close"
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="rounded-lg p-1.5 text-hms-textDim hover:bg-white/5 hover:text-hms-text"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <SearchFilterPanel filters={filters} onChange={setFilters} />
          </div>
        </>
      )}
    </div>
  );
}
